import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type ActionEventRow,
  type ActionOutcomeRow,
  type AdminActionDetail,
  useAdminAction,
  useReenrichAction,
} from '@/lib/admin-actions'
import { formatExact } from '@/lib/format'
import { formatMoneyPrecise } from '@/lib/money'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, Field, JsonBlock, stamp, Tag, Th } from '../-parts'

export const Route = createFileRoute('/_app/admin/actions/$actionId')({
  component: ActionPage,
})

/**
 * One action, whole.
 *
 * This is the screen where somebody works out why a detector said something
 * stupid, so it withholds nothing: the frozen evidence, the contract it was
 * opened against, its subjects, its timeline, every reading taken since, and
 * the model run that described it. Completeness beats polish here — a blob the
 * machine wrote, rendered as a blob, is more use than a curated half of it.
 */
function ActionPage() {
  const { actionId } = Route.useParams()
  const { data, isPending, error } = useAdminAction(actionId)
  const enrich = useReenrichAction(actionId)

  if (error) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          title="Action not found"
          description="No action on this deployment carries that id."
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/admin/actions">Back to actions</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (isPending || !data) return <Skeleton className="h-96 w-full" />

  const { action, site, organization, run } = data

  return (
    <>
      <DetailHeader
        back={
          <Link to="/admin/actions" className={BACK_LINK}>
            <ArrowLeftIcon className="size-3" aria-hidden />
            Actions
          </Link>
        }
        title={action.headline ?? action.title}
        meta={
          <>
            <span className="font-mono text-[12px] text-muted-foreground">{action.type}</span>
            <Tag>{action.nature}</Tag>
            <Tag tone="plain">{action.state}</Tag>
            <Tag>{action.source}</Tag>
            {action.suppressed && <Tag tone="warn">suppressed</Tag>}
            <span className="font-mono text-[12px]">{site?.host ?? action.siteId}</span>
            <span className="text-[12px] text-muted-foreground">
              {organization?.name ?? action.organizationId}
            </span>
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            {enrich.isSuccess && (
              <span className="font-mono text-[11px] text-muted-foreground">
                queued {enrich.data?.job}
              </span>
            )}
            {/* Past the evidence hash on purpose: a rescan does not pay to be
                told the same thing twice, and this is the exception for when the
                wording rather than the numbers is what is wrong. */}
            <Button
              size="sm"
              variant="outline"
              disabled={enrich.isPending}
              onClick={() => enrich.mutate()}
            >
              Describe it again
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {action.skippedReason && (
          <Panel title="Vetoed by the model">
            <p className="text-[13px]">{action.skippedReason}</p>
          </Panel>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="What it says">
            <div className="space-y-3 text-[13px]">
              {/* The detector's own wording is always here, whatever the model
                  did or did not manage. */}
              <p className="text-muted-foreground">{action.title}</p>
              {action.summary && <p>{action.summary}</p>}
              {action.steps && action.steps.length > 0 && (
                <ol className="list-decimal space-y-1 pl-5">
                  {action.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              )}
              {action.suggestion && (
                <p className="rounded-md border border-border bg-secondary/30 p-3 font-mono text-[12px]">
                  {action.suggestion}
                </p>
              )}
            </div>
          </Panel>

          <Panel title="Identity">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Id" wide>
                {action.id}
              </Field>
              <Field label="Subject key" wide>
                {action.subjectKey}
              </Field>
              <Field label="Subject kind">{action.subjectKind}</Field>
              <Field label="Effort">{action.effort}</Field>
              <Field label="Evidence hash" wide>
                {action.evidenceHash}
              </Field>
              <Field label="Value (clicks/mo)">
                {action.valueClicks === null ? null : formatExact(Math.round(action.valueClicks))}
              </Field>
              <Field label="Priority">{Math.round(action.priority)}</Field>
              <Field label="Confidence">
                {action.confidence === null ? null : action.confidence.toFixed(2)}
              </Field>
              <Field label="Parent">{action.parentActionId}</Field>
            </dl>
          </Panel>
        </div>

        <Panel title="Lifecycle">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field label="Detected">{stamp(action.detectedAt)}</Field>
            <Field label="Last seen">{stamp(action.lastSeenAt)}</Field>
            <Field label="Enriched">{stamp(action.enrichedAt)}</Field>
            <Field label="Measure after">{stamp(action.measureAfter)}</Field>
            <Field label="Verified">{stamp(action.verifiedAt)}</Field>
            <Field label="Verified via">{action.verifiedVia}</Field>
            <Field label="Closed">{stamp(action.closedAt)}</Field>
            <Field label="Close reason">{action.closeReason}</Field>
            <Field label="Created">{stamp(action.createdAt)}</Field>
            <Field label="Updated">{stamp(action.updatedAt)}</Field>
            <Field label="Created by">{action.createdBy}</Field>
            <Field label="Closed by">{action.closedBy}</Field>
          </dl>
        </Panel>

        <Panel title="Subjects" meta={formatExact(data.subjects.length)} flush>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <Th align="left" className="w-[90px]">
                    Role
                  </Th>
                  <Th align="left" className="w-[110px]">
                    Kind
                  </Th>
                  <Th align="left">Label</Th>
                  <Th align="left">Value</Th>
                  <Th className="w-[90px]">Keyword</Th>
                  <Th className="w-[90px]">Page</Th>
                  <Th className="w-[90px]">Domain</Th>
                </tr>
              </thead>
              <tbody>
                {data.subjects.map((subject) => (
                  <tr key={subject.id} className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 pl-3 font-mono text-[12px]">{subject.role}</td>
                    <td className="px-3 py-2 font-mono text-[12px]">{subject.kind}</td>
                    <td className="max-w-[280px] truncate px-3 py-2">{subject.label}</td>
                    <td className="max-w-[280px] truncate px-3 py-2 font-mono text-[12px]">
                      {subject.value}
                    </td>
                    <Id value={subject.queryId} />
                    <Id value={subject.pageId} />
                    <Id value={subject.referringDomainId} />
                  </tr>
                ))}
                {data.subjects.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-[13px] text-muted-foreground">
                      No subject rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Frozen at the moment the claim was made, never re-read: an action
              is a claim made on a date. */}
          <Panel title="Evidence">
            <JsonBlock value={action.evidence} />
          </Panel>
          <Panel title="Baseline">
            <JsonBlock value={action.baseline} />
          </Panel>
        </div>

        <Panel title="Outcomes" meta={formatExact(data.outcomes.length)} flush>
          {data.outcomes.length === 0 ? (
            <p className="px-4 py-4 text-[13px] text-muted-foreground">Nothing measured yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <Th align="left" className="w-[140px]">
                      Measured
                    </Th>
                    <Th align="left" className="w-[180px]">
                      Window
                    </Th>
                    <Th align="left" className="w-[90px]">
                      Metric
                    </Th>
                    <Th className="w-[90px]">Baseline</Th>
                    <Th className="w-[90px]">Current</Th>
                    <Th className="w-[90px]">Delta</Th>
                    <Th className="w-[90px]">Control</Th>
                    <Th className="w-[90px]">Excess</Th>
                    <Th align="left" className="w-[120px]">
                      Verdict
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {data.outcomes.map((outcome) => (
                    <Outcome key={outcome.id} outcome={outcome} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Timeline" meta={formatExact(data.events.length)} flush>
          <ul className="divide-y divide-border">
            {data.events.map((event) => (
              <Event key={event.id} event={event} />
            ))}
            {data.events.length === 0 && (
              <li className="px-4 py-4 text-[13px] text-muted-foreground">No events.</li>
            )}
          </ul>
        </Panel>

        {run && <Run run={run} />}
      </div>
    </>
  )
}

function Id({ value }: { value: number | null }) {
  return (
    <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
      {value === null ? '' : value}
    </td>
  )
}

function metric(value: number | null, kind: string): string {
  if (value === null) return ''
  if (kind === 'ctr') return `${(value * 100).toFixed(2)}%`
  if (kind === 'position') return value.toFixed(1)
  return formatExact(Math.round(value))
}

function signed(value: number | null, kind: string): string {
  if (value === null) return ''
  const sign = value > 0 ? '+' : ''
  return `${sign}${metric(value, kind)}`
}

function Outcome({ outcome }: { outcome: ActionOutcomeRow }) {
  // The paired difference, which is the only number here that says anything
  // about the action rather than about the site's own drift.
  const excess =
    outcome.delta === null || outcome.controlDelta === null
      ? null
      : outcome.delta - outcome.controlDelta

  return (
    <tr className="border-b border-border align-top last:border-b-0">
      <td className="py-2 pr-3 pl-3 font-mono text-[11.5px]">{stamp(outcome.measuredAt)}</td>
      <td className="px-3 py-2 font-mono text-[11.5px] text-muted-foreground">
        {outcome.windowFrom} → {outcome.windowTo}
      </td>
      <td className="px-3 py-2 font-mono text-[12px]">{outcome.metric}</td>
      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {metric(outcome.baselineValue, outcome.metric)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {metric(outcome.currentValue, outcome.metric)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {signed(outcome.delta, outcome.metric)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] text-muted-foreground tabular">
        {signed(outcome.controlDelta, outcome.metric)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {signed(excess, outcome.metric)}
      </td>
      <td className="px-3 py-2">
        <Tag
          tone={
            outcome.verdict === 'improved'
              ? 'good'
              : outcome.verdict === 'declined'
                ? 'bad'
                : 'muted'
          }
        >
          {outcome.verdict}
        </Tag>
        {outcome.detail && <JsonBlock value={outcome.detail} className="mt-2 max-h-[160px]" />}
      </td>
    </tr>
  )
}

function Event({ event }: { event: ActionEventRow }) {
  return (
    <li className="space-y-1 px-4 py-2.5">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-[11.5px] text-muted-foreground tabular">
          {stamp(event.at)}
        </span>
        <Tag tone="plain">{event.kind}</Tag>
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {event.userName ?? event.userId ?? 'system'}
        </span>
      </div>
      {event.message && <p className="text-[13px]">{event.message}</p>}
      {event.detail && <JsonBlock value={event.detail} className="max-h-[200px]" />}
    </li>
  )
}

function Run({ run }: { run: NonNullable<AdminActionDetail['run']> }) {
  return (
    <Panel
      title="Model run"
      actions={
        <Link
          to="/admin/ai/runs/$runId"
          params={{ runId: run.id }}
          className="mono-label text-link hover:underline"
        >
          Transcript
        </Link>
      }
    >
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label="Model" wide>
          {run.modelId}
        </Field>
        <Field label="Status">
          <span className={cn(run.status === 'failed' && 'text-destructive')}>{run.status}</span>
        </Field>
        <Field label="Upstream">{run.upstreamProvider}</Field>
        <Field label="Prompt">
          {run.promptKey === null ? null : `${run.promptKey} v${run.promptVersion ?? '?'}`}
        </Field>
        <Field label="Hook">{run.hook}</Field>
        <Field label="Attempts">{run.attempts}</Field>
        <Field label="Cost">{formatMoneyPrecise(run.costUsd)}</Field>
        <Field label="Finished">{stamp(run.finishedAt)}</Field>
        <Field label="Error" wide>
          {run.error && <span className="text-destructive">{run.error}</span>}
        </Field>
      </dl>
    </Panel>
  )
}
